import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import DashboardLayout from "../../Layout/DashboardLayout";
import { companyID } from "../../Common/UIComponents";
import {
  fetchBookingList,
  approveRejectBooking,
} from "../../API_Service/LabBooking_Slice";
import { employeeID } from "../../Common/UIComponents";
import { getEmployeeID, getCompanyID } from "../../Common/UIComponents";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const toArray = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  return Array.isArray(d) ? d : [];
};

// ─────────────────────────────────────────────
// STATUS CONFIG
// ─────────────────────────────────────────────

const getStatus = (isApproved, statusCode) => {
  if (statusCode === "APPR" || isApproved === "Y")
    return {
      key: "AP",
      label: "Approved",
      dot: "bg-[#436900]",
      badge: "bg-[#bbf46e]/40 text-[#112000]",
    };
  if (statusCode === "REJ")
    return {
      key: "RJ",
      label: "Rejected",
      dot: "bg-[#ba1a1a]",
      badge: "bg-[#ffdad6] text-[#93000a]",
    };
  return {
    key: "TN",
    label: "Tentative",
    dot: "bg-[#6e797a]",
    badge: "bg-[#e5e9e9] text-[#3e494a]",
  };
};

// ─────────────────────────────────────────────
// CONFIRM DIALOG
// ─────────────────────────────────────────────

const ConfirmDialog = ({
  open,
  type, // "approve" | "reject"
  booking,
  hasConflict,
  tentativeConflicts = [],
  onConfirm,
  onCancel,
  isLoading,
}) => {
  const [remarks, setRemarks] = useState("");

  // Clear remarks whenever dialog opens or closes
  useEffect(() => {
    if (!open) setRemarks("");
  }, [open]);

  if (!open || !booking) return null;

  const isApprove = type === "approve";

  // ── Approve theme ──
  const approveTheme = {
    overlay: "bg-black/40",
    iconBg: "bg-[#e8f8d0]",
    iconColor: "text-[#436900]",
    icon: "check_circle",
    accent: "bg-[#bbf46e]",
    accentHover: "hover:bg-[#a0d755]",
    accentText: "text-[#112000]",
    titleColor: "text-[#181c1d]",
    divider: "bg-[#bbf46e]/30",
    label: "Approve",
    labelIcon: "check",
  };

  // ── Reject theme ──
  const rejectTheme = {
    overlay: "bg-black/50",
    iconBg: "bg-[#ffdad6]",
    iconColor: "text-[#ba1a1a]",
    icon: "cancel",
    accent: "bg-[#ba1a1a]",
    accentHover: "hover:bg-[#93000a]",
    accentText: "text-white",
    titleColor: "text-[#181c1d]",
    divider: "bg-[#ffdad6]",
    label: "Reject",
    labelIcon: "block",
  };

  const t = isApprove ? approveTheme : rejectTheme;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center px-4 ${t.overlay} backdrop-blur-[2px]`}
    >
      <div
        className="bg-white rounded-3xl shadow-[0px_32px_64px_rgba(24,28,29,0.18)] w-full max-w-md overflow-hidden"
        style={{
          animation: "dialogPop 0.18s cubic-bezier(0.34,1.56,0.64,1) both",
        }}
      >
        <style>{`
          @keyframes dialogPop {
            from { opacity: 0; transform: scale(0.92) translateY(12px); }
            to   { opacity: 1; transform: scale(1)    translateY(0);    }
          }
        `}</style>

        {/* ── Coloured top strip ── */}
        <div className={`h-1.5 w-full ${t.accent}`} />

        <div className="p-7">
          {/* ── Icon + Title ── */}
          <div className="flex items-start gap-4 mb-5">
            <div
              className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${t.iconBg}`}
            >
              <span
                className={`material-symbols-outlined text-[24px] ${t.iconColor}`}
              >
                {t.icon}
              </span>
            </div>
            <div className="pt-0.5">
              <h3
                className={`text-lg font-extrabold leading-tight ${t.titleColor}`}
              >
                {isApprove
                  ? hasConflict
                    ? "Conflict Detected"
                    : "Approve Booking"
                  : "Reject Booking"}
              </h3>
              <p className="text-xs text-[#6e797a] mt-0.5 font-medium">
                {isApprove ? "Lab Booking Approval" : "Lab Booking Rejection"}
              </p>
            </div>
          </div>

          {/* ── Approved conflict warning ── */}
          {isApprove && hasConflict && (
            <div className="flex items-start gap-3 bg-[#fff8e1] border border-[#ffe082] rounded-xl px-4 py-3 mb-5">
              <span className="material-symbols-outlined text-[#f59e0b] text-[20px] flex-shrink-0 mt-0.5">
                warning
              </span>
              <p className="text-xs text-[#78350f] leading-relaxed font-medium">
                <span className="font-bold">{booking.facilityName}</span> is
                already booked and approved for this time slot. This request
                cannot be approved.
              </p>
            </div>
          )}

          {/* ── Tentative overlap notice ── */}
          {isApprove && tentativeConflicts.length > 0 && (
            <div className="flex items-start gap-3 bg-[#e8f4fd] border border-[#90caf9] rounded-xl px-4 py-3 mb-5">
              <span className="material-symbols-outlined text-[#1565c0] text-[20px] flex-shrink-0 mt-0.5">
                info
              </span>
              <div className="text-xs text-[#0d47a1] leading-relaxed">
                <p className="font-bold mb-1">
                  {tentativeConflicts.length === 1
                    ? "1 other tentative booking exists for the same lab and time."
                    : `${tentativeConflicts.length} other tentative bookings exist for the same lab and time.`}
                </p>
                <ul className="space-y-0.5">
                  {tentativeConflicts.map((b) => (
                    <li key={b.bookingId} className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-[#1565c0] flex-shrink-0" />
                      <span className="font-medium">{b.bookingNo}</span>
                      <span className="text-[#1565c0]/70">
                        — {b.projectName}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-1.5 text-[#1565c0]/80">
                  You can approve either this one or the other — only one should
                  be approved for this time slot.
                </p>
              </div>
            </div>
          )}

          {/* ── Booking summary card ── */}
          <div className="bg-[#f7fafa] rounded-2xl p-4 mb-5 space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#6e797a]">
                Booking No
              </span>
              <span className="text-sm font-extrabold text-[#181c1d]">
                {booking.bookingNo}
              </span>
            </div>
            <div className={`h-px ${t.divider}`} />
            <div className="flex items-start gap-2.5 text-sm text-[#3e494a]">
              <span className="material-symbols-outlined text-[16px] text-[#08757c]/70 mt-0.5 flex-shrink-0">
                science
              </span>
              <span className="font-medium leading-snug">
                {booking.testName}
              </span>
            </div>
            <div className="flex items-center gap-2.5 text-sm text-[#3e494a]">
              <span className="material-symbols-outlined text-[16px] text-[#08757c]/70 flex-shrink-0">
                domain
              </span>
              <span>{booking.facilityName}</span>
            </div>
            <div className="flex items-center gap-2.5 text-sm text-[#3e494a]">
              <span className="material-symbols-outlined text-[16px] text-[#08757c]/70 flex-shrink-0">
                schedule
              </span>
              <span>
                {booking.startDate} — {booking.endDate}
              </span>
            </div>
          </div>

          {/* ── Body message ── */}
          <p className="text-sm text-[#3e494a] leading-relaxed mb-5">
            {isApprove
              ? hasConflict
                ? "Do you still want to approve this booking despite the conflict?"
                : "This will confirm the facility reservation and notify the requester."
              : "This will free up the facility time slot. The requester will be notified of the rejection."}
          </p>

          {/* ── Optional Remarks — Reject only ── */}
          {!isApprove && (
            <div className="mb-6">
              <label className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-widest text-[#6e797a] mb-2">
                Remarks
                <span className="normal-case font-normal text-[#6e797a]/60 tracking-normal">
                  (optional)
                </span>
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Add a reason for rejection…"
                rows={3}
                className="w-full bg-[#f7fafa] border border-[#bdc9c9]/40 rounded-xl px-4 py-3 text-sm text-[#181c1d] placeholder:text-[#6e797a]/50 outline-none focus:ring-2 focus:ring-[#ba1a1a]/30 resize-none transition-shadow"
              />
            </div>
          )}

          {/* ── Actions ── */}
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 py-3 rounded-xl bg-[#e5e9e9] text-[#3e494a] text-sm font-bold hover:bg-[#d7dbdb] transition-colors disabled:opacity-50"
            >
              {isApprove && hasConflict ? "Close" : "Cancel"}
            </button>
            <button
              onClick={() => onConfirm(remarks)}
              disabled={isLoading || (isApprove && hasConflict)}
              className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 ${t.accent} ${t.accentHover} ${t.accentText}`}
            >
              {isLoading ? (
                <>
                  <span className="material-symbols-outlined text-[16px] animate-spin">
                    progress_activity
                  </span>
                  Please wait…
                </>
              ) : isApprove && hasConflict ? (
                <>
                  <span className="material-symbols-outlined text-[16px]">
                    block
                  </span>
                  Cannot Approve
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-[16px]">
                    {t.labelIcon}
                  </span>
                  {t.label}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// BOOKING CARD
// ─────────────────────────────────────────────

const BookingCard = ({ booking, onApprove, onReject }) => {
  const status = getStatus(booking.isApproved, booking.statusCode);
  const isTentative = status.key === "TN";
  const isRejected = status.key === "RJ";

  return (
    <div
      className={`bg-white rounded-2xl p-4 flex flex-col gap-3 shadow-[0px_2px_8px_rgba(24,28,29,0.06)] hover:shadow-[0px_20px_40px_rgba(24,28,29,0.10)] transition-shadow duration-300 ${isRejected ? "opacity-80" : ""}`}
    >
      {/* ── Header ── */}
      <div className="flex justify-between items-start gap-3">
        <div className="min-w-0">
          <span
            title={booking.projectName}
            className="text-[10px] font-extrabold text-[#08757c] uppercase tracking-widest mb-1 block truncate cursor-default"
          >
            {booking.projectName}
          </span>
          <h3 className="font-bold text-[#181c1d] text-base leading-snug">
            {booking.testName}
          </h3>
        </div>
        <span
          className={`flex-shrink-0 text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1.5 ${status.badge}`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${status.dot}`}
          />
          {status.label}
        </span>
      </div>

      {/* ── Details ── */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-3 text-sm text-[#3e494a]">
          <span className="material-symbols-outlined text-[18px] text-[#08757c]/70 flex-shrink-0">
            domain
          </span>
          <span>{booking.facilityName}</span>
        </div>
        <div
          className={`flex items-center gap-3 text-sm text-[#3e494a] ${isRejected ? "line-through opacity-70" : ""}`}
        >
          <span className="material-symbols-outlined text-[18px] text-[#08757c]/70 flex-shrink-0">
            schedule
          </span>
          <span>
            {booking.startDate} — {booking.endDate}
          </span>
        </div>
        <div className="flex items-center gap-3 text-sm text-[#3e494a]">
          <span className="material-symbols-outlined text-[18px] text-[#08757c]/70 flex-shrink-0">
            timer
          </span>
          <span>{booking.totalHrs} hrs</span>
        </div>
      </div>

      {/* ── Booking no ── */}
      <div className="bg-[#f1f4f4] rounded-xl px-4 py-2.5 flex items-center justify-between mt-auto">
        <span className="text-[10px] font-bold text-[#3e494a]/60 uppercase tracking-widest">
          Booking No
        </span>
        <span className="text-sm font-extrabold text-[#181c1d]">
          {booking.bookingNo}
        </span>
      </div>

      {/* ── Action Buttons — Tentative only ── */}
      {isTentative ? (
        <div className="flex items-center gap-3">
          <button
            onClick={() => onReject(booking)}
            className="flex-1 py-2.5 rounded-xl bg-[#e5e9e9] text-[#3e494a] font-semibold text-sm hover:bg-[#ffdad6] hover:text-[#93000a] transition-colors"
          >
            Reject
          </button>
          <button
            onClick={() => onApprove(booking)}
            className="flex-1 py-2.5 rounded-xl bg-[#bbf46e] text-[#112000] font-semibold text-sm hover:bg-[#a0d755] transition-colors shadow-sm"
          >
            Approve
          </button>
        </div>
      ) : (
        <div className="h-10 pointer-events-none opacity-0" />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────

export default function LabBookingRequest() {
  const dispatch = useDispatch();

  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterProject, setFilterProject] = useState("all");
  const [filterFacility, setFilterFacility] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [approveDialog, setApproveDialog] = useState({
    open: false,
    booking: null,
    hasConflict: false,
    tentativeConflicts: [],
  });
  const [rejectDialog, setRejectDialog] = useState({
    open: false,
    booking: null,
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // ── Load bookings ──────────────────────────────────────────────────────────
  const loadBookings = useCallback(() => {
    setIsLoading(true);
    dispatch(fetchBookingList({ companyID }))
      .then((res) => setBookings(toArray(res)))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadBookings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Filter options ─────────────────────────────────────────────────────────
  const projectOptions = useMemo(() => {
    const seen = new Set();
    return bookings.reduce((acc, b) => {
      const key = b.projectCode || b.projectName || "";
      if (!key || seen.has(key)) return acc;
      seen.add(key);
      const label =
        b.projectCode && b.projectName
          ? `${b.projectCode} — ${b.projectName}`
          : b.projectCode || b.projectName || "";
      acc.push({ value: key, label });
      return acc;
    }, []);
  }, [bookings]);

  const facilityOptions = useMemo(
    () => [...new Set(bookings.map((b) => b.facilityName))],
    [bookings],
  );

  // ── Filtered list ──────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return bookings.filter((b) => {
      const status = getStatus(b.isApproved, b.statusCode);
      if (
        filterProject !== "all" &&
        (b.projectCode || b.projectName) !== filterProject
      )
        return false;
      if (filterFacility !== "all" && b.facilityName !== filterFacility)
        return false;
      if (filterStatus !== "all" && status.key !== filterStatus) return false;
      if (
        q &&
        !(b.projectName ?? "").toLowerCase().includes(q) &&
        !b.testName.toLowerCase().includes(q) &&
        !b.facilityName.toLowerCase().includes(q) &&
        !b.bookingNo.toLowerCase().includes(q)
      )
        return false;
      return true;
    });
  }, [bookings, searchQuery, filterProject, filterFacility, filterStatus]);

  // Reset to page 1 whenever filters, search, or page size change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterProject, filterFacility, filterStatus, pageSize]);

  // ── Pagination ─────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  // ── Conflict / overlap helpers ─────────────────────────────────────────────
  const parseBookingDate = (str) => {
    if (!str) return null;
    const [datePart, timePart, meridiem] = str.split(" ");
    const [day, month, year] = datePart.split("-");
    const [hours, minutes] = timePart.split(":");
    let h = parseInt(hours, 10);
    if (meridiem === "PM" && h !== 12) h += 12;
    if (meridiem === "AM" && h === 12) h = 0;
    return new Date(year, month - 1, day, h, parseInt(minutes, 10));
  };

  const overlaps = (s1, e1, s2, e2) => s1 < e2 && e1 > s2 && e1 !== s2;

  const checkConflict = useCallback(
    (booking) => {
      const newStart = parseBookingDate(booking.startDate);
      const newEnd = parseBookingDate(booking.endDate);
      if (!newStart || !newEnd) return false;
      return bookings.some((b) => {
        if (b.bookingId === booking.bookingId) return false;
        if (b.facilityName !== booking.facilityName) return false;
        if (b.statusCode !== "APPR" && b.isApproved !== "Y") return false;
        const s = parseBookingDate(b.startDate);
        const e = parseBookingDate(b.endDate);
        if (!s || !e) return false;
        return overlaps(newStart, newEnd, s, e);
      });
    },
    [bookings],
  ); // eslint-disable-line

  const checkTentativeOverlap = useCallback(
    (booking) => {
      const newStart = parseBookingDate(booking.startDate);
      const newEnd = parseBookingDate(booking.endDate);
      if (!newStart || !newEnd) return [];
      return bookings.filter((b) => {
        if (b.bookingId === booking.bookingId) return false;
        if (b.facilityName !== booking.facilityName) return false;
        if (b.statusCode === "APPR" || b.isApproved === "Y") return false;
        if (b.statusCode === "REJ") return false;
        const s = parseBookingDate(b.startDate);
        const e = parseBookingDate(b.endDate);
        if (!s || !e) return false;
        return overlaps(newStart, newEnd, s, e);
      });
    },
    [bookings],
  ); // eslint-disable-line

  const showToast = (message, isError = false) => {
    setToast({ message, isError });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Approve flow ───────────────────────────────────────────────────────────
  const handleApproveClick = useCallback(
    (booking) => {
      setApproveDialog({
        open: true,
        booking,
        hasConflict: checkConflict(booking),
        tentativeConflicts: checkTentativeOverlap(booking),
      });
    },
    [checkConflict, checkTentativeOverlap],
  );

  // Approve onConfirm receives remarks arg (always empty string for approve — ignored)
  const handleApproveConfirm = useCallback(() => {
    const { booking } = approveDialog;
    if (!booking) return;
    setActionLoading(true);
    dispatch(
      approveRejectBooking({
        bookingID: booking.bookingId,
        approveRejectEmpID: getEmployeeID(),
        actionName: "APPROVE",
        companyID,
      }),
    )
      .then((res) => {
        const result = res?.payload;
        if (result?.success) {
          showToast("Booking approved successfully.");
          loadBookings();
        } else {
          showToast(
            result?.message ?? "Approval failed. Please try again.",
            true,
          );
        }
      })
      .catch(() => showToast("Approval failed. Please try again.", true))
      .finally(() => {
        setApproveDialog({
          open: false,
          booking: null,
          hasConflict: false,
          tentativeConflicts: [],
        });
        setActionLoading(false);
      });
  }, [approveDialog, dispatch, loadBookings]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reject flow ────────────────────────────────────────────────────────────
  const handleRejectClick = useCallback((booking) => {
    setRejectDialog({ open: true, booking });
  }, []);

  // Receives remarks string from dialog (may be empty)
  const handleRejectConfirm = useCallback(
    (remarks = "") => {
      const { booking } = rejectDialog;
      if (!booking) return;
      setActionLoading(true);
      dispatch(
        approveRejectBooking({
          bookingID: booking.bookingId,
          approveRejectEmpID: employeeID,
          actionName: "REJECT",
          remarks: remarks.trim() || null,
          companyID,
        }),
      )
        .then((res) => {
          const result = res?.payload;
          if (result?.success) {
            showToast("Booking rejected.");
            loadBookings();
          } else {
            showToast(
              result?.message ?? "Rejection failed. Please try again.",
              true,
            );
          }
        })
        .catch(() => showToast("Rejection failed. Please try again.", true))
        .finally(() => {
          setRejectDialog({ open: false, booking: null });
          setActionLoading(false);
        });
    },
    [rejectDialog, dispatch, loadBookings],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const tentativeCount = useMemo(
    () =>
      bookings.filter((b) => getStatus(b.isApproved, b.statusCode).key === "TN")
        .length,
    [bookings],
  );

  return (
    <DashboardLayout>
      <div className="px-8 py-5 bg-[#f7fafa] min-h-screen text-[#181c1d]">
        {/* ── Breadcrumb ── */}
        <nav className="flex items-center gap-2 text-xs text-[#3e494a] mb-2">
          <span>Lab Management</span>
          <span className="material-symbols-outlined text-[14px]">
            chevron_right
          </span>
          <span className="text-[#08757c] font-semibold">
            Approvals &amp; History
          </span>
        </nav>

        {/* ── Page Header ── */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-5">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-[#181c1d] mb-1">
              Approvals &amp; History
            </h1>
            <p className="text-[#3e494a] text-sm">
              Manage and review lab booking requests across all active
              facilities.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {tentativeCount > 0 && (
              <span className="text-xs font-bold bg-[#bbf46e]/30 text-[#436900] px-3 py-1.5 rounded-full flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#436900]" />
                {tentativeCount} pending
              </span>
            )}
          </div>
        </div>

        {/* ── Filter Bar ── */}
        <div className="bg-[#f1f4f4] rounded-2xl p-3 mb-5 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-[#3e494a]">
            <span className="material-symbols-outlined text-[16px]">
              filter_list
            </span>
            <span className="text-xs font-extrabold uppercase tracking-widest">
              Filters
            </span>
          </div>
          <div className="h-5 w-px bg-[#bdc9c9]/40 hidden sm:block" />

          <div className="flex flex-1 flex-wrap items-center gap-2">
            {/* Project */}
            <div className="relative w-[160px]">
              <select
                value={filterProject}
                onChange={(e) => setFilterProject(e.target.value)}
                className="w-full appearance-none bg-[#e5e9e9] text-sm text-[#181c1d] font-medium rounded-lg pl-3 pr-8 py-2 outline-none focus:ring-2 focus:ring-[#08757c] cursor-pointer border-none truncate"
              >
                <option value="all">All Projects</option>
                {projectOptions.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
              <span className="material-symbols-outlined absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-[#3e494a] text-[18px]">
                arrow_drop_down
              </span>
            </div>

            {/* Facility */}
            <div className="relative w-[150px]">
              <select
                value={filterFacility}
                onChange={(e) => setFilterFacility(e.target.value)}
                className="w-full appearance-none bg-[#e5e9e9] text-sm text-[#181c1d] font-medium rounded-lg pl-3 pr-8 py-2 outline-none focus:ring-2 focus:ring-[#08757c] cursor-pointer border-none truncate"
              >
                <option value="all">All Facilities</option>
                {facilityOptions.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
              <span className="material-symbols-outlined absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-[#3e494a] text-[18px]">
                arrow_drop_down
              </span>
            </div>

            {/* Status */}
            <div className="relative w-[130px]">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full appearance-none bg-[#e5e9e9] text-sm text-[#181c1d] font-medium rounded-lg pl-3 pr-8 py-2 outline-none focus:ring-2 focus:ring-[#08757c] cursor-pointer border-none"
              >
                <option value="all">All Statuses</option>
                <option value="TN">Tentative</option>
                <option value="AP">Approved</option>
                <option value="RJ">Rejected</option>
              </select>
              <span className="material-symbols-outlined absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-[#3e494a] text-[18px]">
                arrow_drop_down
              </span>
            </div>
          </div>

          {/* Search */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#3e494a]/50 text-[18px] pointer-events-none">
                search
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search project, test, facility…"
                className="bg-white text-sm rounded-lg pl-9 pr-4 py-2 outline-none focus:ring-2 focus:ring-[#08757c] w-56 border border-[#bdc9c9]/30"
              />
            </div>
          </div>
        </div>

        {/* ── Content ── */}
        {isLoading ? (
          <div className="flex items-center justify-center py-24 text-[#3e494a]">
            <span className="material-symbols-outlined text-[32px] animate-spin mr-3">
              progress_activity
            </span>
            Loading bookings…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-[#3e494a]">
            <span className="material-symbols-outlined text-[48px] mb-3 opacity-30">
              inbox
            </span>
            <p className="text-sm font-semibold">No bookings found.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {paginated.map((booking) => (
                <BookingCard
                  key={booking.bookingId}
                  booking={booking}
                  onApprove={handleApproveClick}
                  onReject={handleRejectClick}
                />
              ))}
            </div>

            {/* ── Pagination ── */}
            {filtered.length > 0 && (
              <div className="flex items-center justify-between mt-5 px-1">
                <div className="flex items-center gap-3">
                  <p className="text-xs text-[#6e797a] font-medium">
                    Showing{" "}
                    {Math.min(
                      (currentPage - 1) * pageSize + 1,
                      filtered.length,
                    )}
                    –{Math.min(currentPage * pageSize, filtered.length)} of{" "}
                    {filtered.length} bookings
                  </p>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-[#6e797a]">
                      Rows per page:
                    </span>
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="text-xs font-bold bg-[#e5e9e9] text-[#3e494a] rounded-lg px-2 py-1 outline-none focus:ring-2 focus:ring-[#08757c] cursor-pointer border-none"
                    >
                      {[10, 25, 50].map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="h-8 w-8 flex items-center justify-center rounded-lg bg-[#e5e9e9] text-[#3e494a] hover:bg-[#d7dbdb] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="material-symbols-outlined text-[18px]">
                      chevron_left
                    </span>
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(
                      (p) =>
                        p === 1 ||
                        p === totalPages ||
                        Math.abs(p - currentPage) <= 1,
                    )
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === "..." ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="h-8 w-8 flex items-center justify-center text-xs text-[#9eabac]"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setCurrentPage(p)}
                          className={`h-8 w-8 flex items-center justify-center rounded-lg text-xs font-bold transition-colors
                            ${
                              p === currentPage
                                ? "bg-[#08757c] text-white shadow-sm"
                                : "bg-[#e5e9e9] text-[#3e494a] hover:bg-[#d7dbdb]"
                            }`}
                        >
                          {p}
                        </button>
                      ),
                    )}

                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="h-8 w-8 flex items-center justify-center rounded-lg bg-[#e5e9e9] text-[#3e494a] hover:bg-[#d7dbdb] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="material-symbols-outlined text-[18px]">
                      chevron_right
                    </span>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Approve Dialog ── */}
      <ConfirmDialog
        open={approveDialog.open}
        type="approve"
        booking={approveDialog.booking}
        hasConflict={approveDialog.hasConflict}
        tentativeConflicts={approveDialog.tentativeConflicts}
        isLoading={actionLoading}
        onConfirm={handleApproveConfirm}
        onCancel={() =>
          setApproveDialog({
            open: false,
            booking: null,
            hasConflict: false,
            tentativeConflicts: [],
          })
        }
      />

      {/* ── Reject Dialog ── */}
      <ConfirmDialog
        open={rejectDialog.open}
        type="reject"
        booking={rejectDialog.booking}
        hasConflict={false}
        isLoading={actionLoading}
        onConfirm={handleRejectConfirm}
        onCancel={() => setRejectDialog({ open: false, booking: null })}
      />

      {/* ── Toast ── */}
      {toast && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold
          ${toast.isError ? "bg-[#ba1a1a] text-white" : "bg-[#bbf46e] text-[#112000]"}`}
        >
          <span className="material-symbols-outlined text-[18px]">
            {toast.isError ? "error" : "check_circle"}
          </span>
          {toast.message}
        </div>
      )}
    </DashboardLayout>
  );
}
